import { useEffect, useRef, useState } from "react";

type UnknownStateValue<T> = T | (() => T);

// Ref: https://github.com/react-component/util/blob/master/src/hooks/useMergedState.ts
export function useControlledState<T, R = T>(
  defaultStateValue: UnknownStateValue<T>,
  option?: {
    defaultValue?: UnknownStateValue<T>;
    value?: T;
    onChange?: (value: T, prevValue: T) => void;
    postState?: (value: T) => T;
  },
): [R, (value: T) => void] {
  const { defaultValue, value, onChange, postState } = option || {};
  const [innerValue, setInnerValue] = useState<T>(() => {
    if (value !== undefined) {
      return value;
    }
    if (defaultValue !== undefined) {
      return typeof defaultValue === "function"
        ? (defaultValue as () => T)()
        : defaultValue;
    }
    return typeof defaultStateValue === "function"
      ? (defaultStateValue as () => T)()
      : defaultStateValue;
  });

  let mergedValue = value !== undefined ? value : innerValue;
  if (postState) {
    mergedValue = postState(mergedValue);
  }

  function triggerChange(newValue: T) {
    setInnerValue(newValue);
    if (mergedValue !== newValue && onChange) {
      onChange(newValue, mergedValue);
    }
  }

  // Effect of reset value to `undefined`
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }

    if (value === undefined) {
      setInnerValue(value as React.SetStateAction<T>);
    }
  }, [value]);

  return [mergedValue as unknown as R, triggerChange];
}
